import { createContext, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import { Box, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { PageTitle } from '@components/PageTitle'
import { ExportAllCheckbox } from '@components/ui/ExportAllCheckbox'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { ENTITY_CONFIG_TYPE, FLEXIBLE, VISIBLE_HIDDEN } from '@constants'

import { AllStyleSettings } from './components/AllStyleSettings'
import { useConfigStyleSettings } from './hooks/useConfigStyleSettings'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const ConfigStyleSettings: FC = () => {
  const { t } = useTranslation()

  const {
    state: { allStyleSettings, currentSort },
    data: { isAllStyleSettings, methods, isLoadingStyleSettingsData },
    handlers: {
      handleCreateStyleSetting,
      handleEditStyleSetting,
      handleDeleteStyleSetting,
      handleSetSorting,
    },
  } = useConfigStyleSettings()

  const contextValue = {
    allStyleSettings,
    isLoadingStyleSettingsData,
    currentSort,
    onCreateStyleSetting: handleCreateStyleSetting,
    onEditStyleSetting: handleEditStyleSetting,
    onDeleteStyleSetting: handleDeleteStyleSetting,
    onSetSorting: handleSetSorting,
  }

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.STYLE_SETTINGS}>
        <Outlet />
        <Box sx={{ ...FLEXIBLE, ...(!isAllStyleSettings && VISIBLE_HIDDEN), pb: 1 }}>
          <PageTitle
            end={
              <Box sx={{ py: 0.5 }}>
                <EditConfigMenu />
              </Box>
            }
            start={
              <>
                <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                  {t('pageTitle.styleSettings')}
                </Typography>
              </>
            }
          />
          <PageContentLayout>
            <ExportAllCheckbox />
            <AllStyleSettings methods={methods} />
          </PageContentLayout>
        </Box>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
